import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getAuthToken } from "../utils/auth";

interface SailthruUser {
  keys?: {
    email?: string;
    sid?: string;
  };
  vars?: Record<string, unknown>;
  lists?: Record<string, number>;
  engagement?: string;
  optout_email?: string;
}

interface LookupError {
  id: string;
  error: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const STORAGE_KEY = "sailthru_user_job_id";
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const DEFAULT_PAYLOAD_TEMPLATE = {
  id: "user@example.com",
  key: "email",
  vars: {
    site: {
      cd: {
        personalization_email_recs: [
          "https://www.caranddriver.com/reviews/a71605316/2028-mercedes-benz-vle-class-ev-drive/",
          "https://www.caranddriver.com/news/a71668019/porsche-ceo-plans-more-profits-less-production/",
          "https://www.caranddriver.com/news/a71716075/sanrivatti-hypercar-startup-details/",
        ],
      },
    },
  },
};

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sailthru-users-tabpanel-${index}`}
      aria-labelledby={`sailthru-users-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SailthruUsers: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Lookup state
  const [lookupIds, setLookupIds] = useState("");
  const [lookupKey, setLookupKey] = useState<"email" | "sid">("email");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [users, setUsers] = useState<SailthruUser[]>([]);
  const [lookupErrors, setLookupErrors] = useState<LookupError[]>([]);
  const [selectedUser, setSelectedUser] = useState<SailthruUser | null>(null);

  // Update state
  const [updatePayload, setUpdatePayload] = useState(
    JSON.stringify(DEFAULT_PAYLOAD_TEMPLATE, null, 2)
  );
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<"idle" | "pending" | "completed" | "failed">("idle");
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollStartTimeRef.current = null;
  }, []);

  const checkJobStatus = useCallback(async (id: string) => {
    if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > POLL_TIMEOUT_MS) {
      setJobStatus("failed");
      setUpdateError("Job timed out after 5 minutes");
      stopPolling();
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/sailthru-user/job/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === "completed") {
        setJobStatus("completed");
        setUpdateSuccess("Bulk update completed successfully");
        stopPolling();
        localStorage.removeItem(STORAGE_KEY);
      } else if (data.status === "failed") {
        setJobStatus("failed");
        setUpdateError("Bulk update job failed");
        stopPolling();
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err: any) {
      console.error("Failed to check job status:", err);
    }
  }, [stopPolling]);

  const startPolling = useCallback((id: string) => {
    stopPolling();
    pollStartTimeRef.current = Date.now();
    pollIntervalRef.current = setInterval(() => {
      checkJobStatus(id);
    }, POLL_INTERVAL_MS);
  }, [checkJobStatus, stopPolling]);

  // Load stored job ID on mount
  useEffect(() => {
    const storedJobId = localStorage.getItem(STORAGE_KEY);
    if (storedJobId) {
      setJobId(storedJobId);
      setJobStatus("pending");
      startPolling(storedJobId);
    }
  }, [startPolling]);

  // Cleanup polling on unmount
  useEffect(() => stopPolling, [stopPolling]);

  const handleLookup = async () => {
    if (!lookupIds.trim()) {
      setLookupError("Please enter at least one email or SID");
      return;
    }

    setLookupLoading(true);
    setLookupError(null);
    setUsers([]);
    setLookupErrors([]);

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const ids = encodeURIComponent(lookupIds.trim());
      const response = await fetch(
        `${apiUrl}/prod/sailthru-user?id=${ids}&key=${lookupKey}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setUsers(data.users || []);
      setLookupErrors(data.errors || []);
    } catch (err: any) {
      setLookupError(err.message || "Failed to lookup users");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(null);

    try {
      let payload;
      try {
        payload = JSON.parse(updatePayload);
      } catch {
        throw new Error("Invalid JSON in payload");
      }

      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/sailthru-user`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok && response.status !== 207) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle bulk update response (immediate, not job-based)
      if (data.updated !== undefined) {
        if (data.success) {
          setUpdateSuccess(data.message || `Successfully updated ${data.updated} user(s)`);
        } else {
          setUpdateError(
            `${data.message}${data.errors ? `: ${data.errors.map((e: { id: string; error: string }) => `${e.id} (${e.error})`).join(", ")}` : ""}`
          );
          if (data.updated > 0) {
            setUpdateSuccess(`${data.updated} user(s) updated successfully`);
          }
        }
      } else {
        setUpdateSuccess("User updated successfully");
      }
    } catch (err: any) {
      setUpdateError(err.message || "Failed to update user(s)");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleLoadTemplate = () => {
    setUpdatePayload(JSON.stringify(DEFAULT_PAYLOAD_TEMPLATE, null, 2));
  };

  const handleUseLookupEmail = () => {
    if (users.length === 0) {
      setUpdateError("No users found from lookup. Please search for users first.");
      return;
    }

    const usersWithEmail = users.filter((u) => u.keys?.email);
    if (usersWithEmail.length === 0) {
      setUpdateError("No users with email found in lookup results");
      return;
    }

    try {
      const current = JSON.parse(updatePayload);
      const templatePayload = Array.isArray(current) ? current[0] : current;
      const { id: _id, key: _key, ...restPayload } = templatePayload;

      if (usersWithEmail.length === 1) {
        const payload = {
          id: usersWithEmail[0].keys!.email!,
          key: "email",
          ...restPayload,
        };
        setUpdatePayload(JSON.stringify(payload, null, 2));
      } else {
        const payload = usersWithEmail.map((user) => ({
          id: user.keys!.email!,
          key: "email",
          ...restPayload,
        }));
        setUpdatePayload(JSON.stringify(payload, null, 2));
      }
      setUpdateError(null);
    } catch {
      setUpdateError("Cannot modify payload - invalid JSON");
    }
  };

  const handleUseLookupSid = () => {
    if (users.length === 0) {
      setUpdateError("No users found from lookup. Please search for users first.");
      return;
    }

    const usersWithSid = users.filter((u) => u.keys?.sid);
    if (usersWithSid.length === 0) {
      setUpdateError("No users with SID found in lookup results");
      return;
    }

    try {
      const current = JSON.parse(updatePayload);
      const templatePayload = Array.isArray(current) ? current[0] : current;
      const { id: _id, key: _key, ...restPayload } = templatePayload;

      if (usersWithSid.length === 1) {
        const payload = {
          id: usersWithSid[0].keys!.sid!,
          key: "sid",
          ...restPayload,
        };
        setUpdatePayload(JSON.stringify(payload, null, 2));
      } else {
        const payload = usersWithSid.map((user) => ({
          id: user.keys!.sid!,
          key: "sid",
          ...restPayload,
        }));
        setUpdatePayload(JSON.stringify(payload, null, 2));
      }
      setUpdateError(null);
    } catch {
      setUpdateError("Cannot modify payload - invalid JSON");
    }
  };

  const handleCancelJob = () => {
    stopPolling();
    setJobId(null);
    setJobStatus("idle");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Sailthru Users
      </Typography>

      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Lookup Users" />
          <Tab label="Update Users" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            {/* Lookup Form */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
              <TextField
                label="Email(s) or SID(s)"
                placeholder="Enter comma-separated values"
                value={lookupIds}
                onChange={(e) => setLookupIds(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 300 }}
                helperText="Separate multiple values with commas"
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Key Type</InputLabel>
                <Select
                  value={lookupKey}
                  label="Key Type"
                  onChange={(e) => setLookupKey(e.target.value as "email" | "sid")}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sid">SID</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={lookupLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleLookup}
                disabled={lookupLoading || !lookupIds.trim()}
                sx={{ height: 56 }}
              >
                {lookupLoading ? "Searching..." : "Search"}
              </Button>
            </Box>

            {lookupError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {lookupError}
              </Alert>
            )}

            {lookupErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some users could not be found:{" "}
                {lookupErrors.map((e) => `${e.id} (${e.error})`).join(", ")}
              </Alert>
            )}

            {/* User Results */}
            {users.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>SID</TableCell>
                      <TableCell>Engagement</TableCell>
                      <TableCell>Optout</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user, index) => (
                      <TableRow key={user.keys?.sid || user.keys?.email || index} hover>
                        <TableCell>{user.keys?.email || "—"}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {user.keys?.sid || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.engagement || "unknown"}
                            size="small"
                            color={
                              user.engagement === "active"
                                ? "success"
                                : user.engagement === "disengaged"
                                ? "warning"
                                : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {user.optout_email === "all" ? (
                            <Chip label="Opted Out" size="small" color="error" />
                          ) : user.optout_email === "blast" ? (
                            <Chip label="Blast Only" size="small" color="warning" />
                          ) : (
                            <Chip label="Subscribed" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setSelectedUser(user)}>
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {users.length === 0 && !lookupLoading && !lookupError && lookupIds && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No users found. Try searching for users above.
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Job Status Banner */}
            {jobStatus === "pending" && jobId && (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" onClick={handleCancelJob}>
                    Cancel
                  </Button>
                }
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography>Bulk update in progress... Job ID: {jobId}</Typography>
                </Box>
                <LinearProgress sx={{ mt: 1 }} />
              </Alert>
            )}

            {jobStatus === "completed" && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setJobStatus("idle")}>
                Bulk update completed successfully!
              </Alert>
            )}

            {jobStatus === "failed" && updateError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setJobStatus("idle")}>
                {updateError}
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={handleLoadTemplate}>
                Load Template
              </Button>
              <Button
                variant="outlined"
                onClick={handleUseLookupEmail}
                disabled={users.filter((u) => u.keys?.email).length === 0}
              >
                Use Lookup Email{users.filter((u) => u.keys?.email).length > 0 ? ` (${users.filter((u) => u.keys?.email).length})` : ""}
              </Button>
              <Button
                variant="outlined"
                onClick={handleUseLookupSid}
                disabled={users.filter((u) => u.keys?.sid).length === 0}
              >
                Use Lookup SID{users.filter((u) => u.keys?.sid).length > 0 ? ` (${users.filter((u) => u.keys?.sid).length})` : ""}
              </Button>
            </Box>

            {/* Payload Editor */}
            <TextField
              label="User Update Payload (JSON)"
              multiline
              rows={16}
              fullWidth
              value={updatePayload}
              onChange={(e) => setUpdatePayload(e.target.value)}
              sx={{
                mb: 3,
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: 13,
                },
              }}
              helperText="Enter a single user object or an array of user objects for bulk update"
            />

            {updateError && jobStatus !== "failed" && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateError}
              </Alert>
            )}

            {updateSuccess && jobStatus !== "pending" && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {updateSuccess}
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={
                updateLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />
              }
              onClick={handleUpdate}
              disabled={updateLoading || !updatePayload.trim() || jobStatus === "pending"}
            >
              {updateLoading ? "Updating..." : "Update User(s)"}
            </Button>
          </TabPanel>
        </Box>
      </Paper>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onClose={() => setSelectedUser(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">User Details</Typography>
          <IconButton onClick={() => setSelectedUser(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Keys
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography>{selectedUser.keys?.email || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      SID
                    </Typography>
                    <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      {selectedUser.keys?.sid || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Chip
                    label={`Engagement: ${selectedUser.engagement || "unknown"}`}
                    color={
                      selectedUser.engagement === "active"
                        ? "success"
                        : selectedUser.engagement === "disengaged"
                        ? "warning"
                        : "default"
                    }
                  />
                  <Chip
                    label={
                      selectedUser.optout_email === "all"
                        ? "Opted Out"
                        : selectedUser.optout_email === "blast"
                        ? "Blast Optout"
                        : "Subscribed"
                    }
                    color={
                      selectedUser.optout_email === "all"
                        ? "error"
                        : selectedUser.optout_email === "blast"
                        ? "warning"
                        : "success"
                    }
                  />
                </Box>
              </Box>

              {selectedUser.lists && Object.keys(selectedUser.lists).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Lists
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {Object.entries(selectedUser.lists).map(([listName, status]) => (
                        <Chip
                          key={listName}
                          label={listName}
                          size="small"
                          color={status === 1 ? "primary" : "default"}
                          variant={status === 1 ? "filled" : "outlined"}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {selectedUser.vars && Object.keys(selectedUser.vars).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Variables
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(selectedUser.vars, null, 2)
                          )
                        }
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "grey.100",
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(selectedUser.vars, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SailthruUsers;
